import DOMPurify from "isomorphic-dompurify"

const AI_HTML_TAG_RE =
	/<\/?(?:p|ul|ol|li|strong|em|b|i|code|pre|h[1-6]|br|a|div|span|blockquote|hr|pl-image)\b/i

// The chat renders this HTML with innerHTML / dangerouslySetInnerHTML, so a
// planted <img onerror>, <script>, or javascript: link in a thread would run in
// the reader's (teammate or customer) origin (PRO-11229 C3). Allow only the
// small tag set the chat actually emits; everything else, every event handler,
// and every non-http(s) URL is dropped. pl-image is the placeholder the
// renderer swaps for the real image, so it and its data-id survive.
const AI_SANITIZE_CONFIG = {
	ALLOWED_TAGS: [
		"p",
		"ul",
		"ol",
		"li",
		"strong",
		"em",
		"b",
		"i",
		"code",
		"pre",
		"h1",
		"h2",
		"h3",
		"h4",
		"h5",
		"h6",
		"br",
		"a",
		"div",
		"span",
		"blockquote",
		"hr",
		"pl-image",
	],
	ALLOWED_ATTR: ["href", "target", "rel", "class", "data-id"],
	ADD_TAGS: ["pl-image"],
}

function sanitizeAiHtml(html: string): string {
	return DOMPurify.sanitize(
		html,
		AI_SANITIZE_CONFIG as unknown as Parameters<typeof DOMPurify.sanitize>[1],
	) as unknown as string
}

export function looksLikeHtml(content: string): boolean {
	return AI_HTML_TAG_RE.test(content)
}

const AI_BLOCK_TAG_RE =
	/<\/?(?:p|ul|ol|li|pre|h[1-6]|div|blockquote|hr|pl-image)\b/i

const NUL = String.fromCharCode(0)
const TAG_PLACEHOLDER_RE = new RegExp(`${NUL}(\\d+)${NUL}`, "g")

const AI_INLINE_TAG_RE = /<\/?(?:a|strong|em|b|i|code|span|br)\b[^>]*>/gi

function escapeHtml(text: string): string {
	return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

// A URL reaches the DOM inside a quoted attribute, so an unescaped quote in it
// would break out of `href` and inject attributes of its own. Runs on text that
// escapeHtml already handled, so only the quotes are left to escape.
function escapeAttribute(value: string): string {
	return value.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
}

function formatInline(text: string): string {
	// Markdown with an inline <a> or <code> mixed in keeps the tag as written.
	const tags: string[] = []
	let out = escapeHtml(
		text.replace(
			AI_INLINE_TAG_RE,
			(tag) => `\u0000${tags.push(tag) - 1}\u0000`,
		),
	)
	out = out.replace(
		/`([^`\n]+)`/g,
		'<code class="inline-code-shadow">$1</code>',
	)
	out = out.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
	out = out.replace(/__([^_\n]+)__/g, "<strong>$1</strong>")
	out = out.replace(
		/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g,
		(_match, label: string, url: string) =>
			`<a href="${escapeAttribute(url)}">${label}</a>`,
	)
	return out.replace(
		TAG_PLACEHOLDER_RE,
		(_match, i: string) => tags[Number(i)]!,
	)
}

function isUnorderedItem(line: string): boolean {
	return /^[-*+]\s+/.test(line)
}

function isOrderedItem(line: string): boolean {
	return /^\d+\.\s+/.test(line)
}

function itemText(line: string): string {
	if (isOrderedItem(line)) return line.replace(/^\d+\.\s+/, "")
	return line.replace(/^[-*+]\s+/, "")
}

function flushParagraph(lines: string[], html: string[]): void {
	if (lines.length === 0) return
	html.push(`<p>${formatInline(lines.join("\n")).replace(/\n/g, "<br>")}</p>`)
	lines.length = 0
}

function flushUnordered(items: string[], html: string[]): void {
	if (items.length === 0) return
	html.push(
		`<ul class="accent-bullets">${items
			.map((item) => `<li>${formatInline(item)}</li>`)
			.join("")}</ul>`,
	)
	items.length = 0
}

function flushOrdered(items: string[], html: string[]): void {
	if (items.length === 0) return
	html.push(
		`<ol class="steps-list">${items
			.map((item) => `<li>${formatInline(item)}</li>`)
			.join("")}</ol>`,
	)
	items.length = 0
}

function markdownToAiChatHtml(markdown: string): string {
	const lines = markdown.replace(/\r\n?/g, "\n").split("\n")
	const html: string[] = []
	const paragraph: string[] = []
	const unordered: string[] = []
	const ordered: string[] = []

	for (const line of lines) {
		const trimmed = line.trim()
		if (trimmed.length === 0) {
			flushUnordered(unordered, html)
			flushOrdered(ordered, html)
			flushParagraph(paragraph, html)
			continue
		}
		if (isUnorderedItem(trimmed)) {
			flushOrdered(ordered, html)
			flushParagraph(paragraph, html)
			unordered.push(itemText(trimmed))
			continue
		}
		if (isOrderedItem(trimmed)) {
			flushUnordered(unordered, html)
			flushParagraph(paragraph, html)
			ordered.push(itemText(trimmed))
			continue
		}
		flushUnordered(unordered, html)
		flushOrdered(ordered, html)
		paragraph.push(line)
	}

	flushUnordered(unordered, html)
	flushOrdered(ordered, html)
	flushParagraph(paragraph, html)

	return html.join("")
}

// The result goes straight into the DOM, so a link without a target navigates the
// chat away from whatever the reader was doing.
function hardenLinks(html: string): string {
	return html
		.replace(/<a\b(?![^>]*\btarget=)/gi, '<a target="_blank"')
		.replace(/<a\b(?![^>]*\brel=)/gi, '<a rel="noopener noreferrer"')
}

export function normalizeAiChatHtml(content: string): string {
	if (!content) return content
	return hardenLinks(
		sanitizeAiHtml(
			AI_BLOCK_TAG_RE.test(content) ? content : markdownToAiChatHtml(content),
		),
	)
}
